import React, { useState, useRef, useEffect, useCallback } from 'react';
import { API_BASE } from '@/config/api';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Paperclip,
  ArrowRight,
  AlertCircle,
  FileText,
  Loader2,
  Shield,
  Clock,
  Stethoscope,
  X,
  CheckCircle2,
  Video,
  Building2,
  ClipboardList,
} from 'lucide-react';
import axios from 'axios';
import ProPaywallModal from '@/components/ProPaywallModal';
import SecondOpinionPaywallModal from '@/components/SecondOpinionPaywallModal';
import CarePageHeader from '@/components/CarePageHeader';
import MetaTags from '@/components/SEO/MetaTags';
import {
  getUsage,
  canAskQuestion,
  canAttach,
  recordQuestionUsed,
  recordAttachmentUsed,
  hasEntitlement,
  fetchServerEntitlement,
  syncUsageFromServer,
  setEntitlementActive,
} from '@/utils/secondOpinionAccess';
import './second-opinion.css';

const API = API_BASE;

const STARTERS = [
  {
    label: 'Review my biopsy report',
    prompt:
      'Please review my biopsy report and explain the diagnosis, grade, and what questions I should ask my oncologist.',
  },
  {
    label: 'Compare treatment options',
    prompt:
      'Here is my diagnosis and the treatment plan I was offered. Help me understand the options, trade-offs, and what a second opinion usually checks.',
  },
  {
    label: 'Prepare for my consult',
    prompt:
      'Help me prepare a clear case summary and a short list of questions for a specialist second-opinion consult.',
  },
];

const EMPTY_EXPERT = {
  patient_name: '',
  patient_email: '',
  patient_phone: '',
  cancer_type: '',
  current_diagnosis: '',
  current_treatment: '',
  medical_history: '',
  questions: '',
  urgency: 'standard',
};

function formatReply(text) {
  if (!text) return '';
  return String(text)
    .replace(/^\s*#{1,6}\s+/gm, '')
    .replace(/^\s*-{3,}\s*$/gm, '')
    .replace(/^\s*[-*]\s+/gm, '• ')
    .replace(/\*\*/g, '')
    .replace(/\*/g, '')
    .replace(/\s*[\u2013\u2014]\s*/g, ', ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function latestAssistantBrief(history) {
  for (let i = history.length - 1; i >= 0; i -= 1) {
    const msg = history[i];
    if (msg.role === 'assistant' && !msg.error && msg.content) return msg.content;
  }
  return '';
}

export default function SecondOpinionPromptPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const chatEndRef = useRef(null);
  const [message, setMessage] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [chatHistory, setChatHistory] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState('');
  const [showPaywall, setShowPaywall] = useState(false);
  const [showExpertPaywall, setShowExpertPaywall] = useState(false);
  const [paywallReason, setPaywallReason] = useState('limit');
  const [, setUsageTick] = useState(0);
  const [serverEntitled, setServerEntitled] = useState(false);
  const [expertPlan, setExpertPlan] = useState(null);
  const [showExpertForm, setShowExpertForm] = useState(false);
  const [expertForm, setExpertForm] = useState(EMPTY_EXPERT);
  const [expertSubmitting, setExpertSubmitting] = useState(false);
  const [expertResult, setExpertResult] = useState(null);
  const [myRequests, setMyRequests] = useState([]);
  const [lastPaymentOrderId, setLastPaymentOrderId] = useState(null);
  const [reportExcerpt, setReportExcerpt] = useState('');

  const refreshAccess = useCallback(async () => {
    const data = await fetchServerEntitlement();
    if (!data) return;
    setServerEntitled(Boolean(data.entitled));
    if (data.expert_plan) setExpertPlan(data.expert_plan);
    if (data.entitled) setEntitlementActive('server');
    syncUsageFromServer(data);
    setUsageTick((n) => n + 1);
  }, []);

  const loadMyRequests = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/second-opinion/mine`, {
        withCredentials: true,
        timeout: 20000,
      });
      setMyRequests(data.items || []);
    } catch {
      /* signed-out or unavailable */
    }
  }, []);

  useEffect(() => {
    refreshAccess();
    loadMyRequests();
    (async () => {
      try {
        const { data } = await axios.get(`${API}/second-opinion-ai/sessions/latest`, {
          withCredentials: true,
          timeout: 15000,
        });
        if (data?.session_id && Array.isArray(data.messages) && data.messages.length) {
          setSessionId(data.session_id);
          setChatHistory(
            data.messages.map((m) => ({
              role: m.role,
              content: m.content,
            }))
          );
        }
      } catch {
        /* ignore */
      }
    })();
  }, [refreshAccess, loadMyRequests]);

  useEffect(() => {
    if (location.state?.openPaywall) {
      setPaywallReason(location.state.reason || 'route-guard');
      setShowPaywall(true);
      window.history.replaceState({}, document.title);
    }
    if (location.state?.openExpert) {
      setShowExpertForm(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [chatHistory, loading]);

  useEffect(() => {
    if (!textareaRef.current) return;
    textareaRef.current.style.height = 'auto';
    textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 160)}px`;
  }, [message]);

  const entitled = serverEntitled || hasEntitlement();
  const currentUsage = getUsage();
  const canAsk = canAskQuestion() || entitled;
  const canAttachFile = canAttach() || entitled;
  const remaining = entitled ? null : Math.max(0, 2 - currentUsage.questionsUsed);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;

    if (files.length > 1 || uploadedFiles.length >= 1) {
      alert('Attach one PDF or image at a time for the clearest review.');
      return;
    }

    if (!canAttach() && !entitled) {
      setPaywallReason('limit');
      setShowPaywall(true);
      return;
    }

    const file = files[0];
    const maxSize = 10 * 1024 * 1024;
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'image/jpg',
      'image/webp',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];

    if (file.size > maxSize) {
      alert(`${file.name} is too large. Maximum file size is 10MB.`);
      return;
    }
    if (!allowedTypes.includes(file.type) && !/\.(pdf|docx?|jpe?g|png|webp)$/i.test(file.name)) {
      alert('Please upload a PDF, DOC, DOCX, or image file.');
      return;
    }
    setUploadedFiles([file]);
  };

  const removeFile = () => setUploadedFiles([]);

  const openExpertFlow = () => {
    setShowExpertForm(true);
    if (!entitled) {
      setShowExpertPaywall(true);
    }
  };

  const handleAnalyze = async (e) => {
    e?.preventDefault?.();
    if (!message.trim() && uploadedFiles.length === 0) return;

    const hasAttachment = uploadedFiles.length > 0;
    if (!entitled) {
      if (!canAskQuestion()) {
        setPaywallReason('limit');
        setShowPaywall(true);
        return;
      }
      if (hasAttachment && !canAttach()) {
        setPaywallReason('limit');
        setShowPaywall(true);
        return;
      }
    }

    setLoading(true);
    setPhase(hasAttachment ? 'Reading your report…' : 'Reviewing your case…');

    try {
      let fileContent = null;
      if (uploadedFiles.length > 0) {
        try {
          const formData = new FormData();
          formData.append('files', uploadedFiles[0]);
          const uploadResponse = await axios.post(
            `${API}/second-opinion-ai/upload-files`,
            formData,
            {
              headers: { 'Content-Type': 'multipart/form-data' },
              withCredentials: true,
              timeout: 90000,
            }
          );
          fileContent = uploadResponse.data.extracted_text;
          setReportExcerpt(fileContent || '');
          setPhase('Preparing your second-opinion brief…');
        } catch {
          setChatHistory((prev) => [
            ...prev,
            {
              role: 'assistant',
              content:
                'We could not read that file just now. Try a PDF export of the report, or describe the findings in your own words.',
              error: true,
            },
          ]);
          setLoading(false);
          setPhase('');
          return;
        }
      }

      const userMessage = {
        role: 'user',
        content:
          message ||
          (uploadedFiles.length > 0
            ? 'Please analyze the attached medical report and prepare a second-opinion brief.'
            : ''),
        files: uploadedFiles.map((f) => f.name),
      };
      setChatHistory((prev) => [...prev, userMessage]);

      const response = await axios.post(
        `${API}/second-opinion-ai/chat`,
        {
          message:
            message ||
            'Please analyze the attached medical reports and provide a clear second-opinion style brief for the patient.',
          file_content: fileContent,
          session_id: sessionId,
        },
        {
          withCredentials: true,
          headers: { 'Content-Type': 'application/json' },
          timeout: 90000,
        }
      );

      if (response.data.session_id) setSessionId(response.data.session_id);
      setChatHistory((prev) => [
        ...prev,
        { role: 'assistant', content: response.data.response },
      ]);

      if (typeof response.data.free_remaining === 'number' || response.data.entitled != null) {
        syncUsageFromServer({
          free_remaining: response.data.free_remaining,
          entitled: response.data.entitled,
        });
        if (response.data.entitled) setServerEntitled(true);
        setUsageTick((n) => n + 1);
      } else if (!entitled) {
        recordQuestionUsed();
        if (uploadedFiles.length > 0) recordAttachmentUsed();
        setUsageTick((n) => n + 1);
      }

      setMessage('');
      setUploadedFiles([]);
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
    } catch (error) {
      let userFriendlyMessage =
        'We could not finish that review right now. Please try again in a moment.';

      if (error.response?.status === 401) {
        userFriendlyMessage = 'Please sign in to continue your second opinion.';
        navigate(`/auth?redirect=${encodeURIComponent('/second-opinion')}`);
      } else if (error.response?.status === 429) {
        userFriendlyMessage = 'Please wait a moment before sending another question.';
      } else if (error.response?.status === 503) {
        userFriendlyMessage = 'Second opinion review is temporarily unavailable. Try again shortly.';
      } else if (error.response?.status === 402 || error.response?.status === 403) {
        setPaywallReason('limit');
        setShowPaywall(true);
        userFriendlyMessage =
          'Subscription required for second opinions. Upgrade to ByOnco PRO or purchase an expert written review.';
      } else if (error.code === 'ERR_NETWORK') {
        userFriendlyMessage = 'Check your connection and try again.';
      }

      setChatHistory((prev) => [
        ...prev,
        { role: 'assistant', content: userFriendlyMessage, error: true },
      ]);
    } finally {
      setLoading(false);
      setPhase('');
    }
  };

  const handleExpertSubmit = async (e) => {
    e.preventDefault();
    if (!entitled && !lastPaymentOrderId) {
      setShowExpertPaywall(true);
      return;
    }
    setExpertSubmitting(true);
    try {
      const payload = {
        ...expertForm,
        ai_brief: latestAssistantBrief(chatHistory) || undefined,
        report_excerpt: reportExcerpt || undefined,
        payment_order_id: lastPaymentOrderId || undefined,
        file_names: uploadedFiles.map((f) => f.name),
      };
      const { data } = await axios.post(`${API}/second-opinion`, payload, {
        withCredentials: true,
        timeout: 120000,
      });
      setExpertResult(data);
      setShowExpertForm(false);
      await loadMyRequests();
    } catch (error) {
      if (error.response?.status === 401) {
        navigate(`/auth?redirect=${encodeURIComponent('/second-opinion')}`);
        return;
      }
      if (error.response?.status === 402) {
        setShowExpertPaywall(true);
        return;
      }
      alert(
        error.response?.data?.detail?.message ||
          error.response?.data?.detail ||
          'Could not submit your expert request. Please try again.'
      );
    } finally {
      setExpertSubmitting(false);
    }
  };

  const hasResults = chatHistory.some((m) => m.role === 'assistant' && !m.error);

  return (
    <div className="care-page so-care min-h-screen bg-cream-paper text-forest-ink">
      <MetaTags
        title="Online Cancer Second Opinion | Expert Oncologist Review | ByOnco"
        description="Get a comprehensive cancer second opinion online from leading oncologists. Upload biopsy and scans for an expert multidisciplinary review within 12 hours."
        path="/second-opinion"
      />
      <ProPaywallModal
        open={showPaywall}
        onClose={() => setShowPaywall(false)}
        title={
          paywallReason === 'cta'
            ? 'Unlock unlimited second-opinion reviews'
            : 'Subscription required for second-opinion review'
        }
        subtitle="ByOnco PRO unlocks comprehensive case review, clinical matching, and oncology guidance. Expert written opinions and specialist video consults can also be purchased separately."
        returnPath="/second-opinion"
      />

      <SecondOpinionPaywallModal
        open={showExpertPaywall}
        onClose={() => setShowExpertPaywall(false)}
        reason="expert"
        expertPlan={expertPlan}
        onPaid={async (verification) => {
          setLastPaymentOrderId(verification.order_id || null);
          setShowExpertPaywall(false);
          setShowExpertForm(true);
          await refreshAccess();
        }}
      />

      <div className="so-care-shell">
        <CarePageHeader
          current="/second-opinion"
          kicker="Rapid second opinion"
          title="Case brief in minutes. Expert written review within 12 hours."
          lede="Upload a report or describe your diagnosis for a plain-language brief now, then request a board-certified written second opinion or book a live specialist video consult."
        />

        <div className="so-trust-row" aria-label="What you can expect">
          <div className="so-trust-item">
            <Clock className="size-4" aria-hidden />
            <span>Case brief in minutes</span>
          </div>
          <div className="so-trust-item">
            <Shield className="size-4" aria-hidden />
            <span>Expert written review ≤12h</span>
          </div>
          <div className="so-trust-item">
            <Stethoscope className="size-4" aria-hidden />
            <span>Live specialist video when needed</span>
          </div>
        </div>

        {!entitled && (
          <div className="so-usage" role="status">
            <span>
              ByOnco PRO unlocks comprehensive second opinions and multidisciplinary case review.
            </span>
            <button type="button" onClick={() => navigate('/subscribe?redirect=/second-opinion')}>
              Get PRO
            </button>
          </div>
        )}

        {expertResult && (
          <div className="so-success-banner" role="status">
            <CheckCircle2 className="size-5" aria-hidden />
            <div>
              <strong>Expert request submitted ({expertResult.reference || expertResult.id})</strong>
              <p>
                Status: {expertResult.status}. Target written opinion {expertResult.estimated_time}.
                We emailed your confirmation.
              </p>
            </div>
          </div>
        )}

        {myRequests.length > 0 && (
          <div className="so-requests" aria-label="Your expert requests">
            <h2>Your expert requests</h2>
            <ul>
              {myRequests.slice(0, 5).map((item) => (
                <li key={item.id}>
                  <span className="so-req-ref">{item.reference || item.id.slice(0, 8)}</span>
                  <span className={`so-req-status is-${item.status}`}>{item.status}</span>
                  <span className="so-req-meta">{item.cancer_type}</span>
                  {item.status === 'delivered' && item.clinician_opinion && (
                    <details className="so-req-opinion">
                      <summary>Read written opinion</summary>
                      <p>{formatReply(item.clinician_opinion)}</p>
                    </details>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="so-workspace">
          <section className="so-panel so-panel-chat" aria-label="Case review">
            {chatHistory.length === 0 && !loading ? (
              <div className="so-empty">
                <p className="so-empty-title">Start with what you have</p>
                <p className="so-empty-copy">
                  Pathology, imaging summary, or the plan your team offered. We explain what matters
                  and what to ask next, we do not replace your oncologist.
                </p>
                <div className="so-starters">
                  {STARTERS.map((item) => (
                    <button
                      key={item.label}
                      type="button"
                      onClick={() => setMessage(item.prompt)}
                      className="so-starter"
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="so-thread">
                {chatHistory.map((msg, idx) => (
                  <article
                    key={`${msg.role}-${idx}`}
                    className={`so-bubble ${msg.role === 'user' ? 'is-user' : ''} ${msg.error ? 'is-error' : ''}`}
                  >
                    {msg.files?.length > 0 && (
                      <p className="so-file-chip">
                        <FileText className="size-3.5" aria-hidden />
                        {msg.files.join(', ')}
                      </p>
                    )}
                    <p className="so-bubble-text">
                      {msg.role === 'assistant' ? formatReply(msg.content) : msg.content}
                    </p>
                  </article>
                ))}
                {loading && (
                  <div className="so-bubble so-loading">
                    <Loader2 className="size-4 animate-spin" aria-hidden />
                    <span>{phase || 'Working…'}</span>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>
            )}

            <form className="so-composer" onSubmit={handleAnalyze}>
              {uploadedFiles.length > 0 && (
                <div className="so-attach-row">
                  <FileText className="size-4 shrink-0" aria-hidden />
                  <span className="truncate">{uploadedFiles[0].name}</span>
                  <span className="so-attach-size">
                    ({(uploadedFiles[0].size / 1024 / 1024).toFixed(2)} MB)
                  </span>
                  <button type="button" onClick={removeFile} aria-label="Remove file">
                    <X className="size-4" />
                  </button>
                </div>
              )}

              <textarea
                ref={textareaRef}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => {
                  if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                    e.preventDefault();
                    handleAnalyze(e);
                  }
                }}
                placeholder="Describe your diagnosis, stage, and what you were told, or attach a report"
                rows={3}
                disabled={loading}
                aria-label="Second opinion question"
              />

              <div className="so-composer-actions">
                <div className="so-composer-left">
                  <button
                    type="button"
                    className="so-ghost-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadedFiles.length >= 1 || !canAttachFile || loading}
                    title="Attach one report"
                  >
                    <Paperclip className="size-4" />
                    Attach report
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp"
                    onChange={handleFileChange}
                    className="sr-only"
                    tabIndex={-1}
                  />
                </div>
                <button
                  type="submit"
                  className="so-primary-btn"
                  disabled={loading || !canAsk || (!message.trim() && uploadedFiles.length === 0)}
                >
                  {loading ? (
                    <>
                      <Loader2 className="size-4 animate-spin" /> Analyzing
                    </>
                  ) : (
                    <>
                      Get my brief <ArrowRight className="size-4" />
                    </>
                  )}
                </button>
              </div>
            </form>

            <p className="so-disclaimer">
              <AlertCircle className="size-3.5" aria-hidden />
              Educational second-opinion support to help you walk into clinic prepared. Decisions stay with your care team.
            </p>
          </section>

          <aside className="so-panel so-panel-next" aria-label="Next steps">
            <h2>After your brief</h2>
            <ol className="so-steps">
              <li>
                <span className="so-step-num">1</span>
                <div>
                  <strong>Read the brief with someone you trust</strong>
                  <p>Note what is clear, what is uncertain, and what to confirm with your doctor.</p>
                </div>
              </li>
              <li>
                <span className="so-step-num">2</span>
                <div>
                  <strong>Request an expert written opinion</strong>
                  <p>Board-certified oncology review with a target turnaround within 12 hours.</p>
                </div>
              </li>
              <li>
                <span className="so-step-num">3</span>
                <div>
                  <strong>Or book a live specialist video</strong>
                  <p>Discuss your case face-to-face when you want a real-time conversation.</p>
                </div>
              </li>
            </ol>

            <div className="so-next-actions">
              <button
                type="button"
                className="so-primary-btn so-full"
                onClick={openExpertFlow}
              >
                <ClipboardList className="size-4" />
                Request expert written opinion
              </button>
              <button
                type="button"
                className="so-secondary-btn so-full"
                onClick={() => navigate('/teleconsultation')}
              >
                <Video className="size-4" />
                Book specialist video consult
              </button>
              <Link to="/find-hospitals" className="so-secondary-btn so-full">
                <Building2 className="size-4" />
                Find matching hospitals
              </Link>
              {hasResults && (
                <div className="so-done-note">
                  <CheckCircle2 className="size-4" aria-hidden />
                  Your case brief is ready above. Attach it to an expert request or take it into clinic.
                </div>
              )}
            </div>
          </aside>
        </div>

        {showExpertForm && (
          <section className="so-expert-panel" aria-label="Expert written second opinion">
            <div className="so-expert-head">
              <div>
                <h2>Expert written second opinion</h2>
                <p>
                  Share the essentials of your case. A board-certified oncology reviewer prepares a
                  written opinion, target within 12 hours. Your latest case brief is attached automatically when available.
                </p>
              </div>
              <button type="button" className="so-ghost-btn" onClick={() => setShowExpertForm(false)}>
                Close
              </button>
            </div>

            <form className="so-expert-form" onSubmit={handleExpertSubmit}>
              <label>
                Patient name
                <input
                  required
                  value={expertForm.patient_name}
                  onChange={(e) => setExpertForm((f) => ({ ...f, patient_name: e.target.value }))}
                />
              </label>
              <label>
                Email for delivery
                <input
                  type="email"
                  value={expertForm.patient_email}
                  onChange={(e) => setExpertForm((f) => ({ ...f, patient_email: e.target.value }))}
                  placeholder="you@email.com"
                />
              </label>
              <label>
                WhatsApp / phone
                <input
                  value={expertForm.patient_phone}
                  onChange={(e) => setExpertForm((f) => ({ ...f, patient_phone: e.target.value }))}
                />
              </label>
              <label>
                Cancer type
                <input
                  required
                  value={expertForm.cancer_type}
                  onChange={(e) => setExpertForm((f) => ({ ...f, cancer_type: e.target.value }))}
                  placeholder="e.g. Breast, NSCLC, Colon"
                />
              </label>
              <label className="so-span-2">
                Current diagnosis
                <textarea
                  required
                  rows={3}
                  value={expertForm.current_diagnosis}
                  onChange={(e) =>
                    setExpertForm((f) => ({ ...f, current_diagnosis: e.target.value }))
                  }
                />
              </label>
              <label className="so-span-2">
                Current treatment (if any)
                <textarea
                  rows={2}
                  value={expertForm.current_treatment}
                  onChange={(e) =>
                    setExpertForm((f) => ({ ...f, current_treatment: e.target.value }))
                  }
                />
              </label>
              <label className="so-span-2">
                Medical history
                <textarea
                  rows={2}
                  value={expertForm.medical_history}
                  onChange={(e) =>
                    setExpertForm((f) => ({ ...f, medical_history: e.target.value }))
                  }
                />
              </label>
              <label className="so-span-2">
                Questions for the specialist
                <textarea
                  rows={3}
                  value={expertForm.questions}
                  onChange={(e) => setExpertForm((f) => ({ ...f, questions: e.target.value }))}
                />
              </label>
              <label>
                Urgency
                <select
                  value={expertForm.urgency}
                  onChange={(e) => setExpertForm((f) => ({ ...f, urgency: e.target.value }))}
                >
                  <option value="standard">Standard (within 12 hours)</option>
                  <option value="urgent">Urgent, flag for priority queue</option>
                </select>
              </label>

              <div className="so-expert-actions so-span-2">
                {!entitled && (
                  <button
                    type="button"
                    className="so-secondary-btn"
                    onClick={() => setShowExpertPaywall(true)}
                  >
                    Pay for expert review first
                  </button>
                )}
                <button type="submit" className="so-primary-btn" disabled={expertSubmitting}>
                  {expertSubmitting ? (
                    <>
                      <Loader2 className="size-4 animate-spin" /> Submitting
                    </>
                  ) : (
                    <>
                      Submit expert request <ArrowRight className="size-4" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </section>
        )}
      </div>
    </div>
  );
}
